import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from "@dnd-kit/core";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, DollarSign, Calendar, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";

interface Deal {
  id: string;
  title: string;
  value: number;
  currency: string;
  stage: string;
  probability: number;
  expected_close_date: string;
  contact_id: string;
  description: string;
}

const STAGES = [
  { id: "prospecting", name: "Prospecting", color: "bg-blue-500" },
  { id: "qualification", name: "Qualification", color: "bg-purple-500" },
  { id: "proposal", name: "Proposal", color: "bg-yellow-500" },
  { id: "negotiation", name: "Negotiation", color: "bg-orange-500" },
  { id: "won", name: "Won", color: "bg-green-500" },
  { id: "lost", name: "Lost", color: "bg-red-500" },
];

function DealCard({ deal }: { deal: Deal }) {
  return (
    <Card className="p-4 cursor-move hover:shadow-md transition-shadow">
      <div className="space-y-2">
        <h4 className="font-semibold">{deal.title}</h4>
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-primary">
            {deal.currency} {deal.value.toLocaleString()}
          </span>
          <Badge variant="outline">{deal.probability}%</Badge>
        </div>
        {deal.expected_close_date && (
          <div className="flex items-center text-sm text-muted-foreground">
            <Calendar className="h-3 w-3 mr-1" />
            {format(new Date(deal.expected_close_date), "MMM dd, yyyy")}
          </div>
        )}
      </div>
    </Card>
  );
}

function DroppableStage({ stage, deals }: { stage: typeof STAGES[0]; deals: Deal[] }) {
  const stageDeals = deals.filter(d => d.stage === stage.id);
  const totalValue = stageDeals.reduce((sum, deal) => sum + deal.value, 0);

  return (
    <div className="min-w-[300px] flex-shrink-0">
      <div className="glass-card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${stage.color}`} />
            <h3 className="font-semibold">{stage.name}</h3>
            <Badge variant="secondary">{stageDeals.length}</Badge>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          <DollarSign className="inline h-3 w-3 mr-1" />
          ${totalValue.toLocaleString()}
        </div>
        <div className="space-y-2">
          {stageDeals.map(deal => (
            <DealCard key={deal.id} deal={deal} />
          ))}
          {stageDeals.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No deals in this stage
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const DealsPipeline = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [activeDeal, setActiveDeal] = useState<Deal | null>(null);
  const [newDeal, setNewDeal] = useState({
    title: "",
    value: 0,
    currency: "USD",
    stage: "prospecting",
    probability: 50,
    expected_close_date: "",
    description: "",
  });
  const queryClient = useQueryClient();

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ["crm-deals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crm_deals")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as Deal[];
    },
  });

  const createDealMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("crm_deals")
        .insert({
          ...newDeal,
          created_by: user.id,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Deal created successfully");
      queryClient.invalidateQueries({ queryKey: ["crm-deals"] });
      setIsCreateOpen(false);
      setNewDeal({
        title: "",
        value: 0,
        currency: "USD",
        stage: "prospecting",
        probability: 50,
        expected_close_date: "",
        description: "",
      });
    },
    onError: (error) => {
      toast.error("Failed to create deal: " + error.message);
    },
  });

  const updateStageMutation = useMutation({
    mutationFn: async ({ dealId, newStage }: { dealId: string; newStage: string }) => {
      const { error } = await supabase
        .from("crm_deals")
        .update({ stage: newStage })
        .eq("id", dealId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-deals"] });
    },
  });

  const handleDragStart = (event: DragStartEvent) => {
    const deal = deals.find(d => d.id === event.active.id);
    setActiveDeal(deal || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const dealId = active.id as string;
      const newStage = over.id as string;
      updateStageMutation.mutate({ dealId, newStage });
    }
    
    setActiveDeal(null);
  };

  if (isLoading) {
    return <div className="text-center py-12">Loading deals...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Sales Pipeline</h3>
          <p className="text-sm text-muted-foreground">Drag and drop deals to update stages</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Deal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Deal</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Deal Title</Label>
                <Input
                  value={newDeal.title}
                  onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
                  placeholder="Enter deal title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Value</Label>
                  <Input
                    type="number"
                    value={newDeal.value}
                    onChange={(e) => setNewDeal({ ...newDeal, value: parseFloat(e.target.value) })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select value={newDeal.currency} onValueChange={(value) => setNewDeal({ ...newDeal, currency: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Probability (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={newDeal.probability}
                    onChange={(e) => setNewDeal({ ...newDeal, probability: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Expected Close Date</Label>
                  <Input
                    type="date"
                    value={newDeal.expected_close_date}
                    onChange={(e) => setNewDeal({ ...newDeal, expected_close_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={newDeal.description}
                  onChange={(e) => setNewDeal({ ...newDeal, description: e.target.value })}
                  placeholder="Deal details..."
                  rows={3}
                />
              </div>
              <Button onClick={() => createDealMutation.mutate()} className="w-full">
                Create Deal
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map(stage => (
            <DroppableStage key={stage.id} stage={stage} deals={deals} />
          ))}
        </div>
        <DragOverlay>
          {activeDeal && <DealCard deal={activeDeal} />}
        </DragOverlay>
      </DndContext>
    </div>
  );
};